import React, { useEffect, useMemo } from 'react';
import {
  formatTimezoneLabel,
  getTimezoneOptionsForLocation,
  inferTimezoneFromLocation,
} from '@court-sync/shared';

export interface TimezoneSelectFieldProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  country?: string;
  stateOrProvince?: string;
  name?: string;
  id?: string;
  error?: string;
  className?: string;
  autoInfer?: boolean;
}

export function TimezoneSelectField({
  value,
  onChange,
  onBlur,
  country,
  stateOrProvince,
  name = 'timezone',
  id = 'timezone',
  error,
  className = 'input',
  autoInfer = true,
}: TimezoneSelectFieldProps) {
  const options = useMemo(() => {
    const base = getTimezoneOptionsForLocation(country, stateOrProvince);
    if (value && !base.some((option) => option.value === value)) {
      return [{ value, label: formatTimezoneLabel(value) }, ...base];
    }
    return base;
  }, [country, stateOrProvince, value]);

  const inferredTimezone = useMemo(
    () => inferTimezoneFromLocation(country, stateOrProvince),
    [country, stateOrProvince]
  );

  useEffect(() => {
    if (!autoInfer || !inferredTimezone) return;
    if (value === inferredTimezone) return;
    onChange(inferredTimezone);
  }, [autoInfer, inferredTimezone, onChange, value]);

  const selectValue = value || '';

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        Timezone
      </label>
      <select
        id={id}
        name={name}
        value={selectValue}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
        className={className}
        aria-label="Timezone"
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : undefined}
      >
        <option value="">Select timezone</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        Used for court hours and scheduling. We suggest a timezone based on your location — change it if needed.
      </p>
      {error ? (
        <p id={`${id}-error`} className="mt-1 text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      ) : null}
    </div>
  );
}
